import { Core, Decorative } from "@faire/design-tokens";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { Calendar60 } from "@faire/web/slate/icons/CalendarDays";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import CuratedSelectionIcon from "@faire/retailer/packages/core-icons/values/Collections.svg";
import FreeReturnsIcon from "@faire/retailer/packages/core-icons/values/FreeReturn.svg";
import { termsOfServiceLinkFormatting } from "@faire/retailer/views/product/Components/__internal__/utils";

const MobileFaireDirectPerksCardBase: React.FC = () => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const { isFaireDirectCreditPercentageBased, formattedFixedBasedAmount } =
    useFaireDirectFirstOrderIncentive();

  const faireDirectBrandName = faireDirectBrand?.name;

  return (
    <Wrapper>
      <Typography variant="pageHeaderSerifRegular" align="center">
        {isFaireDirectCreditPercentageBased ? (
          <StrictLocalMsg
            defaultMessage="{brandName} invited you to Faire!"
            description={{
              text: "a brand invites a retailer to sign up on Faire",
            }}
            values={DEPRECATED_backwardsCompatibleValues({
              brandName: faireDirectBrandName,
            })}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Lock in {promoMoney} off your first order with {brandCompanyName}*"
            description={{
              text: "Advertising discount with a given brand. The asterisk refers to a disclaimer",
            }}
            values={{
              promoMoney: formattedFixedBasedAmount,
              brandCompanyName: faireDirectBrandName ?? "",
            }}
          />
        )}
      </Typography>
      <Spacer height="1x" />
      <Typography color={Core.text.subdued} align="center">
        <StrictLocalMsg
          defaultMessage="Plus, join thousands of retailers shopping unique vendors on the Faire marketplace."
          description={{
            text: "'Plus' as in 'and more'. This sentence goes right after some promotion advertising",
          }}
        />
      </Typography>
      <Spacer height="4x" />
      <Perks />
      <Spacer height="4x" />
      <Typography
        variant="labelSansRegular"
        color={Core.text.subdued}
        align="center"
      >
        <StrictLocalMsg
          defaultMessage="Eligible for new retailers only. By proceeding, you agree to our <TextLinkFormatting>Terms of Service</TextLinkFormatting>"
          values={{
            TextLinkFormatting: termsOfServiceLinkFormatting,
          }}
          description={{
            text: "Terms and privacy policy disclaimer message for proceeding in sign up process",
            img: "https://cdn.faire.com/fastly/88d45a9b5d2886575380320cc0d34d9f9adac4a307b86bc5521f86f7967864f2.png",
          }}
        />
      </Typography>
    </Wrapper>
  );
};

const Perks: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();

  const perks = [
    {
      id: 1,
      icon: (
        <img
          src={CuratedSelectionIcon}
          alt="Easy reordering with year-round shopping"
          height="20"
          width="20"
        />
      ),
      text: strictLocalize({
        defaultMessage: "Easy reordering with year-round shopping",
        description: {
          text: "One of the benefits Faire offers. Faire offers shipping and reorders on products and brands any time of the year.",
        },
      }),
    },
    {
      id: 2,
      icon: (
        <img
          src={FreeReturnsIcon}
          alt="Free returns for risk-free buying"
          height="20"
          width="20"
        />
      ),
      text: strictLocalize({
        defaultMessage: "Free returns for risk-free buying",
        description: {
          text: "One of the benefits Faire offers. Retailers get free returns to reduce risk when buying new products.",
        },
      }),
    },
    {
      id: 3,
      icon: (
        <Calendar60
          height="20px"
          width="20px"
          titleAccess="60-day payment terms"
        />
      ),
      text: strictLocalize({
        defaultMessage: "60-day payment terms",
        description: {
          text: "For retailers | Desktop and moweb | Displays on logged-out product details page | Faire Direct banner | 1 of 3 value props | Preceded by this header: Get {promoMoney} off {brandCompanyName} | Placeholders in header show the amount a retailer can save on their first order with the brand.",
          img: "https://cdn.faire.com/fastly/23671006803146ba72458ec8ef451bf1cef9d4b5a1405d56cd591f530cb3b725.png",
        },
      }),
    },
  ];

  return (
    <Column gap="3x">
      {perks.map((perk) => (
        <Row key={perk.id} align="center">
          <Icon>{perk.icon}</Icon>
          <Spacer width="2x" />
          <Perk>{perk.text}</Perk>
        </Row>
      ))}
    </Column>
  );
};

export const MobileFaireDirectPerksCard = observer(
  MobileFaireDirectPerksCardBase
);

const Wrapper = styled(Column)`
  background-color: ${Decorative.tint.neutral};
  padding: ${getSpacing("3x", "3x", "4x")};
`;

const Icon = styled(Row).attrs({ align: "center", justify: "center" })`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${Core.surface.inverse};
`;

const Perk = styled(Typography)`
  max-width: 164px;
`;
